/** Tipos y utilidades del dashboard YouTube Analytics (datos desde API). */

export type YoutubeTrendDir = "up" | "down" | "flat";

export interface YoutubeKpiPill {
  value: number;
  labelKey: string;
  direction?: YoutubeTrendDir;
  decimals?: number;
  compact?: boolean;
}

export interface YoutubeTimeSeriesDay {
  label: string;
  iso: string;
  subscribersTotal?: number;
  views?: number;
  videosPosted?: number;
  subscribersGained?: number;
  subscribersLost?: number;
  likes?: number;
  dislikes?: number;
  comments?: number;
  shares?: number;
}

export interface YoutubeVideoRow {
  id: string;
  title: string;
  typeLabel: string;
  dateLabel: string;
  timeLabel: string;
  views: number;
  watchTimeHours: number;
  likes: number;
  comments: number;
  shares: number;
  impressions: number;
  ctr: number;
  avgViewDurationSec: number;
  durationSeconds: number;
  thumbnailColor: string;
  thumbnailUrl?: string;
  isNew?: boolean;
}

export interface YoutubeDemographicSlice {
  labelKey: string;
  pct: number;
  color: string;
}

export interface YoutubeAgeBar {
  rangeKey: string;
  value: number;
}

export interface YoutubeCountrySlice {
  countryKey: string;
  pct: number;
  color: string;
}

export interface YoutubeTrafficSource {
  sourceKey: string;
  visitors: number;
  pct: number;
}

export interface YoutubeCompetitorRow {
  id: string;
  name: string;
  subscribers: number;
  views: number;
  videos: number;
}

export interface YoutubeEngagementSummary {
  views: YoutubeKpiPill;
  likes: YoutubeKpiPill;
  dislikes: YoutubeKpiPill;
  comments: YoutubeKpiPill;
  shares: YoutubeKpiPill;
  videos: YoutubeKpiPill;
}

export interface YoutubeAnalyticsSnapshot {
  channelName: string;
  channelDisplayName: string;
  channelAvatarUrl: string;
  periodSinceLabel: string;
  periodUntilLabel: string;
  community: {
    growth: {
      subscribers: YoutubeKpiPill;
      views: YoutubeKpiPill;
      revenue: YoutubeKpiPill;
      videos: YoutubeKpiPill;
    };
    growthSeries: YoutubeTimeSeriesDay[];
    balance: {
      gained: YoutubeKpiPill;
      lost: YoutubeKpiPill;
      videos: YoutubeKpiPill;
    };
    balanceSeries: YoutubeTimeSeriesDay[];
    viewersByCountry: YoutubeCountrySlice[];
    trafficSources: YoutubeTrafficSource[];
  };
  demographics: {
    gender: YoutubeDemographicSlice[];
    age: YoutubeAgeBar[];
  };
  publishedVideos: {
    summary: YoutubeEngagementSummary;
    series: YoutubeTimeSeriesDay[];
  };
  videosWithViews: {
    summary: YoutubeEngagementSummary;
    series: YoutubeTimeSeriesDay[];
  };
  competitors: {
    rows: YoutubeCompetitorRow[];
  };
  videosTable: YoutubeVideoRow[];
  meta?: { source?: string };
}

const MONTHS_ES = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

export const formatDateLabel = (iso: string) => {
  const [, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS_ES[(m ?? 1) - 1]} ${iso.slice(0, 4)}`;
};

export const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const formatCompactNumber = (n: number, decimals = 0): string => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(decimals || 1).replace(".", ",")}M`;
  }
  if (abs >= 1_000) {
    return `${(n / 1_000).toFixed(decimals || 1).replace(".", ",")}k`;
  }
  return new Intl.NumberFormat("es-CL", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(n);
};

export const formatWatchTime = (hours: number): string => {
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  return `${hours.toFixed(1).replace(".", ",")} h`;
};
